import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import type {TFunction} from 'i18next';
import {type CaptureStatus, type WebcamRefTypes} from '@chekinapp/ui';
import {STATUSES} from '@chekin/core';
import {Analytics, ANALYTIC_EVENTS} from '../../analytics';
import type {FacePosition} from '../faceDetection';
import type {FaceGuideState} from './FaceGuide';
import {useAutoFaceCapture} from './useAutoFaceCapture';
import {useAutoDocumentCapture} from './useAutoDocumentCapture';

const MAX_AUTO_CAPTURE_FAILURES = 2;

function getFallbackReason(autoGaveUp: boolean, manualFallback: boolean) {
  if (autoGaveUp) return 'too_many_failures';
  if (manualFallback) return 'timeout_no_capture';
  return 'user_skipped_auto';
}

function getFaceGuideState(
  isModelLoading: boolean,
  facePosition: FacePosition,
): FaceGuideState {
  if (isModelLoading) return 'searching';
  if (facePosition === 'center') return 'centered';
  if (facePosition === 'unknown') return 'searching';
  return 'aligning';
}

function getFaceHint(faceGuideState: FaceGuideState, t: TFunction) {
  if (faceGuideState === 'centered') return t('hold_still');
  if (faceGuideState === 'aligning') return t('center_your_face');
  return t('position_your_face');
}

function getDocumentHint(isCaptureReady: boolean, t: TFunction) {
  return isCaptureReady ? t('hold_still') : t('place_document_inside_frame');
}

type UseAutoCaptureProps = {
  enabled: boolean;
  isFace: boolean;
  videoRef: RefObject<WebcamRefTypes>;
  processState: STATUSES;
  onCapture: () => void;
  onUnavailable?: () => void;
};

export function useAutoCapture({
  enabled,
  isFace,
  videoRef,
  processState,
  onCapture,
  onUnavailable,
}: UseAutoCaptureProps) {
  const {t} = useTranslation('identity-verification');
  const autoFailuresRef = useRef(0);
  const [autoGaveUp, setAutoGaveUp] = useState(false);

  const face = useAutoFaceCapture({
    videoRef,
    enabled: enabled && isFace && !autoGaveUp,
    onCapture,
  });
  const doc = useAutoDocumentCapture({
    videoRef,
    enabled: enabled && !isFace && !autoGaveUp,
    onCapture,
  });

  const manualFallback = isFace ? face.manualFallback : doc.manualFallback;
  const start = isFace ? face.start : doc.start;
  const mechanism = isFace
    ? face.isUsingClientDetection
      ? 'CLIENT'
      : 'SERVER'
    : 'SERVER';

  const handleManualShutter = useCallback(() => {
    if (enabled) {
      Analytics.track(
        isFace
          ? ANALYTIC_EVENTS.ivSelfieAutoCaptureFallback
          : ANALYTIC_EVENTS.ivDocumentAutoCaptureFallback,
        {
          mechanism,
          reason: getFallbackReason(autoGaveUp, manualFallback),
          failed_attempts: autoFailuresRef.current,
        },
      );
    }
    onCapture();
  }, [enabled, isFace, mechanism, autoGaveUp, manualFallback, onCapture]);

  useEffect(() => {
    if (!enabled || processState !== STATUSES.error) return;
    autoFailuresRef.current += 1;
    if (autoFailuresRef.current >= MAX_AUTO_CAPTURE_FAILURES) setAutoGaveUp(true);
  }, [processState, enabled]);

  const isActive = enabled && !manualFallback && !autoGaveUp;

  const unavailable = enabled && (manualFallback || autoGaveUp);
  useEffect(() => {
    if (unavailable) onUnavailable?.();
  }, [unavailable, onUnavailable]);

  const faceGuideState = getFaceGuideState(face.isModelLoading, face.facePosition);
  const hint = isFace
    ? getFaceHint(faceGuideState, t)
    : doc.guidance ?? getDocumentHint(doc.isCaptureReady, t);

  const detectionReady = isFace ? faceGuideState === 'centered' : doc.isCaptureReady;
  const cameraStatus: CaptureStatus = detectionReady ? 'success' : 'scanning';

  return {isActive, faceGuideState, hint, cameraStatus, handleManualShutter, start};
}
